// Small helper around sessionStorage for the Orenda SSO session.
// Everything the app needs to know about "am I logged in via Cognito?"
// goes through this file.

const KEY = "orendaAuth";

// Refresh anything with less than this many seconds of life left before the
// next API call. Cognito tokens live 300s so this gives us ~1min headroom.
const REFRESH_BUFFER_SEC = 60;

// Guard against multiple concurrent refresh attempts firing (e.g. if several
// API calls hit at the same moment).
let inFlightRefresh = null;

const decodeExp = (idToken) => {
  try {
    const payload = idToken.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(normalized));
    return typeof claims.exp === "number" ? claims.exp : null;
  } catch {
    return null;
  }
};

export const saveOrendaAuth = ({
  id_token,
  access_token,
  refresh_token,
  expires_in,
  token_type,
  scope,
}) => {
  window.sessionStorage.setItem(
    KEY,
    JSON.stringify({
      id_token,
      access_token,
      refresh_token,
      expires_in,
      token_type,
      scope,
      obtainedAt: Date.now(),
    })
  );
};

export const readOrendaAuth = () => {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearOrendaAuth = () => {
  window.sessionStorage.removeItem(KEY);
};

// Returns true when we have an id_token whose `exp` claim is still in the future.
// A 30-second buffer prevents "just expired" tokens from being sent to the API.
export const isOrendaSessionValid = () => {
  return getOrendaSessionStatus().valid;
};

// Same check as isOrendaSessionValid() but returns { valid, reason, exp, nowSec }
// so the UI can explain why a session was rejected.
export const getOrendaSessionStatus = () => {
  const auth = readOrendaAuth();
  if (!auth) return { valid: false, reason: "no orendaAuth in sessionStorage" };
  if (!auth.id_token) return { valid: false, reason: "id_token missing from stored session" };
  const exp = decodeExp(auth.id_token);
  if (!exp) return { valid: false, reason: "could not decode `exp` claim from id_token (likely malformed / OCR'd token)" };
  const nowSec = Math.floor(Date.now() / 1000);
  if (exp - 30 <= nowSec) {
    const diff = nowSec - exp;
    return {
      valid: false,
      reason: `id_token expired ${diff}s ago (exp=${exp}, now=${nowSec})`,
      exp,
      nowSec,
    };
  }
  return { valid: true, reason: "ok", exp, nowSec };
};

// Called before every service call. Reads the cached session, checks how many
// seconds are left on the id_token, and if it's under the buffer triggers a
// silent renewal via the UserManager (which uses the refresh_token).
//
// Returns the (possibly refreshed) auth object. Throws SsoSessionExpired if
// there's no session at all or the refresh_token itself is exhausted — the
// caller should catch this and prompt the user to sign in again.
//
// Deliberately loaded lazily to avoid a circular import with orendaUserManager.
export const ensureOrendaTokensFresh = async () => {
  const auth = readOrendaAuth();
  if (!auth?.id_token) throw new SsoSessionExpired("no session");
  const exp = decodeExp(auth.id_token);
  if (!exp) throw new SsoSessionExpired("could not decode id_token");
  const nowSec = Math.floor(Date.now() / 1000);
  const secondsLeft = exp - nowSec;

  if (secondsLeft > REFRESH_BUFFER_SEC) return auth;

  if (!inFlightRefresh) {
    inFlightRefresh = (async () => {
      // Lazy import — orendaUserManager imports from this file, so a top-level
      // import would create a cycle.
      const { orendaUserManager } = await import("./orendaUserManager");
      try {
        const refreshed = await orendaUserManager.signinSilent();
        if (!refreshed?.id_token) {
          throw new SsoSessionExpired("signinSilent returned no user");
        }
        // The addUserLoaded handler in orendaUserManager already mirrors the
        // new tokens into sessionStorage. Return the freshest copy.
        return readOrendaAuth();
      } catch (err) {
        // Anything from the refresh flow — network, refresh_token expired,
        // Cognito revoke — surfaces as a session-expired to the caller.
        throw new SsoSessionExpired(err?.message || "silent renew failed");
      } finally {
        inFlightRefresh = null;
      }
    })();
  }
  return inFlightRefresh;
};

// Distinct error class so callers can catch it specifically and show a
// "please sign in again" UI instead of a generic error toast.
export class SsoSessionExpired extends Error {
  constructor(reason) {
    super(`Orenda SSO session expired: ${reason}`);
    this.name = "SsoSessionExpired";
    this.code = "SSO_SESSION_EXPIRED";
  }
}
